import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { colors } from "../styles";
import { CloseOutlined, PictureOutlined, FileTextOutlined, FormOutlined, RobotOutlined } from '@ant-design/icons';
import { auth } from '../config/firebase';
import { aiService } from '../services/ai.service';
import { useToast } from '../contexts/ToastContext';

const AddPhotoModal = ({ addNewPhoto, closeModal }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    imgSrc: "",
    isPublic: true
  });
  const [aiPrompt, setAiPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const toast = useToast();

  const handleAIGenerate = async () => {
    setIsLoading(true);
    try {
      const generatedData = await aiService.generateDream(aiPrompt);
      
      if (!generatedData.title || !generatedData.description || !generatedData.imgSrc) {
        throw new Error('La respuesta de la IA está incompleta');
      }

      setFormData({
        ...formData,
        title: generatedData.title,
        description: generatedData.description,
        imgSrc: generatedData.imgSrc,
        isPublic: formData.isPublic
      });

      toast.showToast('¡Sueño generado con éxito!', 'success');
    } catch (error) {
      console.error('Error en handleAIGenerate:', error);
      toast.showToast(error.message || 'Error al generar el sueño', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newCard = {
      ...formData,
      userId: auth.currentUser.uid
    };
    addNewPhoto(newCard);
    closeModal();
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setFormData(prev => ({ ...prev, imgSrc: url }));
    
    // Actualizar preview
    if (url.trim()) {
      setImagePreview(url);
    } else {
      setImagePreview(null);
    }
  };

  // Actualizar el preview cuando la IA genera una imagen
  useEffect(() => {
    if (formData.imgSrc) {
      setImagePreview(formData.imgSrc);
    }
  }, [formData.imgSrc]);

  return (
    <ModalOverlay onClick={closeModal}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={closeModal}>
          <CloseOutlined style={{ fontSize: '20px' }} />
        </CloseButton>
        <ModalTitle>Agregar Nuevo Sueño</ModalTitle>

        {/* Sección de AI */}
        <AISection>
          <Input
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            placeholder="Describe tu sueño y deja que la IA te ayude a visualizarlo..."
          />
          <GenerateButton 
            onClick={handleAIGenerate}
            disabled={isLoading || !aiPrompt.trim()}
          >
            <RobotOutlined /> {isLoading ? 'Generando...' : 'Generar con IA'}
          </GenerateButton>
          <AIHint>
            Ejemplo: "Quiero viajar a Japón y aprender sobre su cultura"
          </AIHint>
        </AISection>

        <Divider>o llena los campos manualmente</Divider>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>
              <PictureOutlined style={{ marginRight: '8px' }} />
              URL de la imagen
            </Label>
            <Input
              type="text"
              value={formData.imgSrc}
              onChange={handleImageUrlChange}
              placeholder="https://ejemplo.com/imagen.jpg"
              required
            />
            {imagePreview && (
              <ImagePreviewContainer>
                <ImagePreview 
                  src={imagePreview} 
                  alt="Preview"
                  onError={() => setImagePreview(null)}
                />
              </ImagePreviewContainer>
            )}
          </FormGroup>
          <FormGroup>
            <Label>
              <FormOutlined style={{ marginRight: '8px' }} />
              Título
            </Label>
            <Input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Mi sueño es..."
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>
              <FileTextOutlined style={{ marginRight: '8px' }} />
              Descripción
            </Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe tu sueño..."
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={formData.isPublic}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  isPublic: e.target.checked
                }))}
              />
              Hacer pública esta tarjeta
            </Label>
          </FormGroup>
          <ButtonGroup>
            <CancelButton type="button" onClick={closeModal}>
              Cancelar
            </CancelButton>
            <SubmitButton type="submit">
              Agregar
            </SubmitButton>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  z-index: 1100;
  overflow-y: auto;
`;

const ModalContent = styled.div`
  background: ${colors.white};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 6px 24px ${colors.shadow};
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;

  /* Estilizar scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.text}10;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.secondary}40;
    border-radius: 4px;
  }
`;

const ModalTitle = styled.h2`
  font-family: 'Caveat', cursive;
  color: ${colors.text};
  font-size: 2rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Label = styled.label`
  font-family: 'Indie Flower', cursive;
  color: ${colors.text};
  font-size: 1.1rem;
`;

const Input = styled.input`
  padding: 0.8rem;
  border: 2px solid ${colors.secondary}40;
  border-radius: 4px;
  font-size: 1rem;
  font-family: 'Indie Flower', cursive;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${colors.secondary};
  }
`;

const Textarea = styled.textarea`
  padding: 0.8rem;
  border: 2px solid ${colors.secondary}40;
  border-radius: 4px;
  font-size: 1rem;
  font-family: 'Indie Flower', cursive;
  min-height: 80px;
  resize: vertical;
  max-height: 150px;

  &:focus {
    outline: none;
    border-color: ${colors.secondary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const Button = styled.button`
  padding: 0.8rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Indie Flower', cursive;
`;

const SubmitButton = styled(Button)`
  background-color: ${colors.secondary};
  color: ${colors.white};
  border: none;

  &:hover {
    background-color: ${colors.accent};
    transform: translateY(-2px);
  }
`;

const CancelButton = styled(Button)`
  background-color: transparent;
  color: ${colors.text};
  border: 2px solid ${colors.secondary}40;

  &:hover {
    border-color: ${colors.secondary};
    transform: translateY(-2px);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  color: ${colors.text};
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const AISection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const GenerateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.8rem;
  background-color: ${colors.secondary};
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: 'Indie Flower', cursive;
  font-size: 1.1rem;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background-color: ${colors.accent};
    transform: translateY(-2px);
  }

  &:disabled {
    background-color: ${colors.text}80;
    cursor: not-allowed;
  }
`;

const AIHint = styled.p`
  color: ${colors.text}80;
  font-size: 0.9rem;
  font-style: italic;
  text-align: center;
`;

const Divider = styled.div`
  text-align: center;
  margin: 1rem 0;
  color: ${colors.text};
  font-family: 'Indie Flower', cursive;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    width: 30%;
    height: 1px;
    background-color: ${colors.text}40;
  }

  &::before {
    left: 0;
  }

  &::after {
    right: 0;
  }
`;

const ImagePreviewContainer = styled.div`
  margin-top: 0.5rem;
  width: 100%;
  height: 150px;
  border-radius: 8px;
  overflow: hidden;
  background-color: ${colors.text}10;
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s ease;
`;

export { AddPhotoModal };
